import React, { useState } from "react";
import { useAuth } from "../AuthProvider";

const BeiUploadForm = () => {
  const { token } = useAuth();
  const [files, setFiles] = useState([]);
  const [message, setMessage] = useState("");
  const [result, setResult] = useState(null);
  const [isUploading, setIsUploading] = useState(false);

  const [step, setStep] = useState(""); // "upload" | "snapshot" | ""
  const [snapshotResult, setSnapshotResult] = useState(null);

  const handleFileChange = (e) => {
    const selected = Array.from(e.target.files || []);
    setFiles(selected);
    setMessage("");
    setResult(null);
    setSnapshotResult(null);
  };

  // ✅ snapshot pakai querystring (bukan body)
  const runSnapshot = async ({ tradeDate, engine = "v1" } = {}) => {
    const headers = {};
    if (token) headers["Authorization"] = `Bearer ${token}`;

    const qs = new URLSearchParams();
    if (tradeDate) qs.set("trade_date", tradeDate); // "2026-01-12"
    qs.set("engine", engine);

    const res = await fetch(`/api/stocks/snapshot-signals?${qs.toString()}`, {
      method: "POST",
      headers,
    });

    const data = await res.json();
    if (!res.ok) throw new Error(data.detail || "Snapshot signals gagal");
    return data;
  };

  const handleUpload = async () => {
    if (!files.length) {
      setMessage("Pilih minimal 1 file .xlsx terlebih dahulu.");
      return;
    }

    const formData = new FormData();
    files.forEach((f) => formData.append("files", f));

    setIsUploading(true);
    setMessage("");
    setResult(null);
    setSnapshotResult(null);

    try {
      const headers = {};
      if (token) headers["Authorization"] = `Bearer ${token}`;

      setStep("upload");

      const res = await fetch("/api/upload-bei-daily", {
        method: "POST",
        headers,
        body: formData,
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Upload gagal");

      const uploadedFiles = data.files || [];
      setResult(uploadedFiles);

      // ✅ ambil daftar tanggal yang baru diupload
      const uploadedDates = Array.from(
        new Set(uploadedFiles.map((x) => x.trade_date).filter(Boolean))
      ).sort();

      if (!uploadedDates.length) {
        setMessage("Upload data BEI berhasil ✅\nNamun tidak ada trade_date yang terbaca dari response.");
        return;
      }

      setMessage(
        `Upload data BEI berhasil ✅\nMenjalankan Snapshot Signal (EOD) untuk ${uploadedDates.length} hari...`
      );

      // ✅ run snapshot per tanggal
      setStep("snapshot");
      const snaps = [];
      for (const td of uploadedDates) {
        const snap = await runSnapshot({ tradeDate: td, engine: "v1" });
        snaps.push(snap);
      }

      setSnapshotResult(snaps);

      // ✅ message ringkas hasil snapshot
      const lines = snaps.map((s) => {
        const td = s.trade_date || "-";
        const rows = s.rows_saved ?? s.total ?? "-";
        const brok = s.broksum_date_used_for_penalty || "-";
        return `- ${td} rows:${rows} broksum:${brok}`;
      });

      setMessage(
        `Upload data BEI berhasil ✅\nSnapshot Signal berhasil ✅\n` + lines.join("\n")
      );
    } catch (err) {
      setMessage(`Error: ${err.message}`);
    } finally {
      setIsUploading(false);
      setStep("");
    }
  };

  return (
    <div className="text-sm text-gray-100">
      <div className="space-y-3">
        <div>
          <label className="block text-xs font-medium mb-1">
            Pilih file Ringkasan Saham BEI (.xlsx)
          </label>
          <input
            type="file"
            accept=".xlsx"
            multiple
            onChange={handleFileChange}
            className="block w-full text-xs text-gray-200
                       file:mr-3 file:py-1.5 file:px-3
                       file:rounded-md file:border-0
                       file:text-xs file:font-medium
                       file:bg-indigo-600 file:text-white
                       hover:file:bg-indigo-500"
          />
          <p className="mt-1 text-[11px] text-gray-400">
            Bisa upload banyak file sekaligus (multi hari).
          </p>
        </div>

        <button
          onClick={handleUpload}
          disabled={isUploading || !files.length}
          className={`w-full py-2 px-3 rounded-lg text-sm font-semibold
            ${
              isUploading || !files.length
                ? "bg-gray-600 text-gray-300 cursor-not-allowed"
                : "bg-indigo-600 hover:bg-indigo-500 text-white"
            }`}
        >
          {isUploading
            ? step === "snapshot"
              ? "Menjalankan snapshot..."
              : "Mengupload & memproses..."
            : "Upload & Proses"}
        </button>

        {message && <p className="mt-1 text-xs whitespace-pre-line">{message}</p>}

        {result && result.length > 0 && (
          <div className="mt-2 border-t border-gray-700 pt-2">
            <h3 className="text-xs font-semibold mb-1">Hasil upload:</h3>
            <ul className="space-y-1 max-h-40 overflow-y-auto text-xs">
              {result.map((r) => (
                <li key={r.filename} className="flex flex-col">
                  <span className="font-mono text-[11px]">{r.filename}</span>
                  <span>
                    Tanggal: <span className="font-mono">{r.trade_date}</span>, rows:{" "}
                    {r.rows_inserted}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

export default BeiUploadForm;
